import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { isNullOrUndefined } from '@app/shared/helpers';
import { PaymentStatus } from '@models/appointments/payment-status';
import { Invoice } from '@models/invoice/invoice';
import { InvoiceType } from '@models/invoice/invoice-type';
import { ObservationTypes } from '@models/observation/observation';
import { Package } from '@models/package';
import { ClinicServiceTemplate } from '@models/service/clinic-service-template';
import { Service } from '@models/service/service';
import { ServiceDetailTemplate } from '@models/service/service-detail-template';
import { ServiceListType } from '@models/service/service-list-type';
import { ServiceNote } from '@models/service/service-note';
import { PlannedTreatment } from '@models/treatment-planning/planned-treatment';
import { PlannedTreatmentMultiple } from '@models/treatment-planning/planned-treatment-multiple';
import { TreatmentPlan } from '@models/treatment-planning/treatment-plan';
import { TreatmentState } from '@models/treatment-planning/treatment-state';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { DataSourceRequestState } from '@progress/kendo-data-query';
import { CurrentDataService } from '@services/currentData.service';
import { InvoicesService } from '@services/invoices.service';
import { PackagesService } from '@services/packages.service';
import { PatientService } from '@services/patient.service';
import { ServiceTemplatesService } from '@services/service-templates.service';
import { ServicesService } from '@services/services.service';
import { TreatmentPlanFormService } from '@services/treatment-planning/treatment-plan-form.service';
import { TreatmentPlanService } from '@services/treatment-planning/treatment-plan.service';
import { Observable, Subject, merge } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { CreateTreatmentPlanPackageComponent } from '../../../create-treatment-plan-package/create-treatment-plan-package.component';

export const DATE_FORMAT = {
  parse: {
    dateInput: 'yyyy-MM-dd',
  },
  display: {
    dateInput: 'yyyy-MM-dd',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-planned-treatments-list',
  templateUrl: './planned-treatments-list.component.html',
  styleUrls: ['./planned-treatments-list.component.less'],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT },
  ],
})
export class PlannedTreatmentsListComponent implements OnInit, OnDestroy {
  treatmentPlan: TreatmentPlan;
  unsub = new Subject<any>();
  treatmentPlanForm: FormGroup;
  packages: Package[];
  serviceTemplates: ClinicServiceTemplate[];
  serviceTemplateMultiples: ClinicServiceTemplate[];
  plannedTreatmentMultiples: PlannedTreatmentMultiple[];
  plannedTreatmentsToDelete: number[];
  completedTreatments: PlannedTreatment[];
  serviceList: Service[];
  loading = false;
  formLoaded = false;
  patientId: number;
  focusServices$ = new Subject<string>();
  focusPackages$ = new Subject<string>();
  focusCustomPackages$ = new Subject<string>();
  gridView: GridDataResult;
  gridState: DataSourceRequestState;
  errors: any[] = [];

  @ViewChild('autosize') autosize: CdkTextareaAutosize;
  @ViewChild('serviceTypeahead') serviceTypeahead: ElementRef<HTMLInputElement>;
  @ViewChild('packageTypeahead') packageTypeahead: ElementRef<HTMLInputElement>;
  @ViewChild('serviceMultipleTypeahead') serviceMultipleTypeahead: ElementRef<HTMLInputElement>;

  PaymentStatus = PaymentStatus;
  ServiceDetailTemplate = ServiceDetailTemplate;
  treatmentState = TreatmentState;
  observationTypes = ObservationTypes;
  serviceListType = ServiceListType;

  constructor(
    private patientService: PatientService,
    private fb: FormBuilder,
    private serviceTemplatesService: ServiceTemplatesService,
    private servicesService: ServicesService,
    private treatmentPlanService: TreatmentPlanService,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialog: MatDialog,
    private treatmentPlanFormService: TreatmentPlanFormService,
    private _ngZone: NgZone,
    private createPackageDialog: MatDialog,
    private invoicesService: InvoicesService,
    private currentDataService: CurrentDataService,
    private packagesService: PackagesService
  ) {
    this.serviceTemplates = [];
    this.serviceTemplateMultiples = [];
    this.plannedTreatmentsToDelete = [];
    this.serviceList = [];
    this.packages = [];
  }

  ngOnInit() {
    this.loading = true;
    this.patientId = +this.route.snapshot.params.patId.split('_')[0];
    this.completedTreatments = [];

    this.patientService.thePatientUpdated$.pipe(takeUntil(this.unsub)).subscribe((patient) => {
      this.getTreatment();
    });

    this.treatmentPlanService.treatmentPlanUpdated$.pipe(takeUntil(this.unsub)).subscribe((tp) => {
      this.treatmentPlan = tp;
      this.plannedTreatmentsToDelete = [];
      this.initForm();
      this.loading = false;
    });

    this.serviceTemplatesService.getServiceTemplateList().subscribe((st) => {
      this.serviceTemplates = st;
    });

    this.serviceTemplatesService.getServiceTemplateMultiples().subscribe((st) => {
      this.serviceTemplateMultiples = st;
    });

    this.packagesService.getPackages().subscribe((packages) => {
      this.packages = packages;
    });

    this.getTreatment();
    this.initForm();
  }

  private getTreatment() {
    this.loading = true;
    this.treatmentPlanService.getTreatmentPlanByPatientId(this.patientId).subscribe((tp) => {
      this.treatmentPlan = tp;
      this.initForm();
      this.loading = false;
    });
  }

  private initForm() {
    if (!isNullOrUndefined(this.treatmentPlan)) {
      this.treatmentPlanForm = this.fb.group({
        notes: [this.treatmentPlan.notes],
        plannedTreatments: this.fb.array([]),
      });
      this.initFormPlannedTreatments();
      this.onFormChange();
      this.formLoaded = true;
    }
  }

  private initFormPlannedTreatments() {
    this.serviceList = [];
    const formArray = <FormArray>this.treatmentPlanForm.get('plannedTreatments');
    if (!isNullOrUndefined(this.treatmentPlan.plannedTreatments)) {
      this.treatmentPlan.plannedTreatments.forEach((pt) => {
        // Ignore PlannedTreatmentMultiples
        if (isNullOrUndefined(pt.plannedTreatmentMultipleId)) {
          if (pt.treatmentState === this.treatmentState.Completed) {
            if (!this.completedTreatments.find((ct) => ct.id === pt.id)) {
              this.completedTreatments.push(pt);
            }
          } else {
            if (isNullOrUndefined(pt.service.observations)) {
              pt.service.observations = [];
            }
            this.serviceList.push(pt.service);
            const isRefunded = pt.paymentStatus === PaymentStatus.Refund;
            formArray.push(
              this.fb.group({
                id: [pt.id],
                rank: [pt.rank, Validators.required],
                service: this.fb.group(pt.service),
                concern: [pt.concern],
                notes: [{ value: pt.notes, disabled: isRefunded }],
                createdDate: [pt.createdDate],
                updatedDate: [pt.updatedDate],
                plannedDate: [pt.plannedDate],
                scheduledServiceId: [pt.scheduledServiceId],
                dueDate: [{ value: pt.dueDate, disabled: isRefunded }],
                treatmentState: [pt.treatmentState],
                treatmentAppointments: [pt.treatmentAppointments],
                treatmentPlanId: [this.treatmentPlan.id],
                price: [
                  {
                    value: pt.service.governmentBilling
                      ? 0
                      : pt.service.chargeAmount != null
                      ? pt.service.chargeAmount
                      : pt.service.overrideDefaultPrice != null
                      ? pt.service.overrideDefaultPrice
                      : pt.service.defaultPrice != null
                      ? pt.service.defaultPrice
                      : 0,
                    disabled: isRefunded,
                  },
                  Validators.required,
                ],
                paymentStatus: [pt.paymentStatus],
              })
            );
          }
        }
      });
    } else {
      this.treatmentPlan.plannedTreatments = [];
    }
    this.plannedTreatmentMultiples = this.treatmentPlan.plannedTreatmentMultiples;
    this.plannedTreatmentMultiples.forEach(
      (ptm) => (ptm['partialRefund'] = ptm.plannedTreatments.some((pt) => pt.paymentStatus == PaymentStatus.Refund))
    );
    this.loading = false;
  }

  private onFormChange() {
    this.treatmentPlanForm.valueChanges.subscribe(() => {
      const plannedTreatments: PlannedTreatment[] = [];
      this.getPlannedTreatmentControls().forEach((pt, idx) => {
        const plannedTreatment = new PlannedTreatment(pt.value);
        plannedTreatment.service = this.serviceList[idx];
        if (plannedTreatment.service.serviceDetailTemplateId === this.ServiceDetailTemplate.None) {
          plannedTreatment.service.defaultPrice = pt.value.price;
        }
        if (isNullOrUndefined(pt.value.price)) {
          pt.value.price = 0;
        }
        plannedTreatments.push(new PlannedTreatment(plannedTreatment));
      });

      this.treatmentPlan.plannedTreatments = plannedTreatments;
      this.treatmentPlan.notes = this.treatmentPlanForm.value.notes;

      this.treatmentPlanFormService.updateTreatmentsofInterest(
        this.treatmentPlan,
        this.plannedTreatmentsToDelete,
        this.treatmentPlanForm.valid
      );
    });
  }

  public getPlannedTreatmentControls() {
    if (isNullOrUndefined(this.treatmentPlan)) {
      return this.fb.array([]).controls;
    }
    return (<FormArray>this.treatmentPlanForm.get('plannedTreatments')).controls;
  }

  async onAddService(partialServiceTemplate: ClinicServiceTemplate) {
    this.serviceTypeahead.nativeElement.blur();
    const serviceTemplate = await this.serviceTemplatesService
      .getServiceTemplateById(partialServiceTemplate.id)
      .toPromise();
    const newService = this.servicesService.mapServiceFromTemplate(
      serviceTemplate,
      this.patientService.patientPanelPatient
    );
    if (isNullOrUndefined(newService.observations)) {
      newService.observations = [];
    }
    // Save the new service
    let plannedTreatment = await this.treatmentPlanService
      .createPlannedTreatment(this.patientId, newService)
      .toPromise();
    this.serviceList.push(plannedTreatment.service);
    this.treatmentPlanFormService.treatmentPlanUpdated(this.treatmentPlan);
  }

  priceUpdated(plannedTreatment: PlannedTreatment, price: number) {
    if (plannedTreatment && plannedTreatment.scheduledServiceId) {
      let index = this.treatmentPlanFormService.scheduledServicePriceUpdate.findIndex(
        (s) => s.serviceId == plannedTreatment.scheduledServiceId
      );
      if (index != -1) {
        this.treatmentPlanFormService.scheduledServicePriceUpdate[index].price = price;
      } else {
        this.treatmentPlanFormService.scheduledServicePriceUpdate.push({
          appointmentId: 0,
          serviceId: plannedTreatment.scheduledServiceId,
          price,
        });
      }
    }
  }

  async updatePlannedTreatment(plannedTreatment: PlannedTreatment, priceInput = null) {
    this.loading = true;

    this.currentDataService.setPreventUserFromNavigation(true);
    if (plannedTreatment.scheduledService && plannedTreatment.scheduledService.observations)
      delete plannedTreatment.scheduledService.observations;
    if (plannedTreatment.service && plannedTreatment.service.observations) delete plannedTreatment.service.observations; //the underlying service is only updated if it has observations - so its done elsewhere

    if (priceInput && priceInput.value) {
      plannedTreatment.service.overrideDefaultPrice = priceInput.value;
    }
    await this.treatmentPlanService.updatePlannedTreatment(plannedTreatment).toPromise();
    this.currentDataService.setPreventUserFromNavigation(false);
    this.loading = false;
  }

  async saveNotes(event) {
    let notes: string = event && event.target && event.target.value ? event.target.value : '';
    await this.treatmentPlanService.updatePatientTreatmentPlanNotes(notes, this.patientId).toPromise();
  }

  public async onDeleteService(index: number, plannedTreatment: PlannedTreatment) {
    if (plannedTreatment.id > 0 && (await this.verifyRemovalOfPlannedTreatment().toPromise())) {
      // The service has been saved in the database so we must remove it from the database as well
      // this.treatmentPlanFormService.treatmentPlanDirty = true;
      // this.plannedTreatmentsToDelete.push(plannedTreatment.id)
      await this.treatmentPlanService.deletePlannedTreatments([plannedTreatment.id]).toPromise();

      (<FormArray>this.treatmentPlanForm.get('plannedTreatments')).removeAt(index);
      this.serviceList.splice(index, 1);
    }
  }

  private verifyRemovalOfPlannedTreatment(): Observable<boolean> {
    const dialogRef = this.createPackageDialog.open(GenericDialogComponent, {
      width: '300px',
      data: {
        title: 'Warning',
        content: 'Are you sure you want to remove this planned treatment?',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCancel: true,
      },
    });

    return dialogRef.afterClosed().pipe(
      takeUntil(this.unsub),
      map((result) => {
        if (result === 'confirm') {
          return true;
        }
        return false;
      })
    );
  }

  onGetServiceNotes(index: number): ServiceNote[] {
    return this.serviceList[index].serviceNotes ? this.serviceList[index].serviceNotes : [];
  }

  onViewServiceDetail(pt: PlannedTreatment) {
    let service = pt.service;
    const treatmentFromToday = true;
    const treatmentPlanning = true;
    this.router.navigate(
      [
        'detail',
        service.serviceId,
        treatmentFromToday,
        treatmentPlanning,
        service.isLocked || pt.paymentStatus != PaymentStatus.Unpaid,
      ],
      {
        relativeTo: this.route.parent,
      }
    );
  }

  triggerResize() {
    // Wait for changes to be applied, then trigger textarea resize.
    this._ngZone.onStable.pipe(take(1)).subscribe(() => this.autosize.resizeToFitContent(true));
  }

  onFocusService(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.focusServices$.next(value);
  }

  onFocusPackages(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.focusPackages$.next(value);
  }

  onFocusCustomPackages(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.focusCustomPackages$.next(value);
  }

  serviceTemplatesInputFormatter = (x: { serviceName: string }) => null;

  serviceTemplatesResultFormatter = (x: { serviceName: string }) => x.serviceName;

  packageInputFormatter = (x: { name: string }) => null;

  packageResultFormatter = (x: { name: string; quantity: number }) => x.name + ' (x' + x.quantity + ')';

  searchServiceTemplates = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const inputFocus$ = this.focusServices$;
    return merge(debouncedText$, inputFocus$).pipe(
      map((term) =>
        term
          ? this.serviceTemplates.filter((v) => v.serviceName.toLowerCase().includes(term.toLowerCase()))
          : this.serviceTemplates
      )
    );
  };

  searchPackages = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    return merge(debouncedText$, this.focusPackages$).pipe(
      map((term) =>
        term ? this.packages.filter((v) => v.name.toLowerCase().includes(term.toLowerCase())) : this.packages
      )
    );
  };

  searchCustomPackages = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    return merge(debouncedText$, this.focusCustomPackages$).pipe(
      map((term) =>
        term
          ? this.serviceTemplateMultiples.filter((v) => v.serviceName.toLowerCase().includes(term.toLowerCase()))
          : this.serviceTemplateMultiples
      )
    );
  };

  async onCreatePackage(pkg: Package) {
    this.packageTypeahead.nativeElement.blur();
    const patient = await this.patientService.getPatientById(this.patientId).toPromise();
    const service = this.servicesService.mapServiceFromTemplate(pkg.serviceTemplate, patient);
    const plannedTreatmentMultiple = PackagesService.convertToMultiple(
      pkg,
      service,
      pkg.serviceTemplate,
      this.patientId,
      this.treatmentPlan.id
    );
    const multiple = await this.treatmentPlanService.addPlannedTreatmentMultiple(plannedTreatmentMultiple).toPromise();
    this.treatmentPlan.plannedTreatmentMultiples.push(multiple);
    this.treatmentPlanService.treatmentPlanUpdated$.next(this.treatmentPlan);
  }

  onCreateCustomPackage(serviceTemplate: ClinicServiceTemplate) {
    this.serviceMultipleTypeahead.nativeElement.blur();
    const dialogRef = this.createPackageDialog.open(CreateTreatmentPlanPackageComponent, {
      panelClass: 'custom-dialog-container',
      width: '500px',
      data: {
        patientId: this.patientId,
        serviceTemplateMultiples: this.serviceTemplateMultiples,
        serviceTemplate: serviceTemplate,
        treatmentPlanId: this.treatmentPlan.id,
        treatmentPlan: this.treatmentPlan,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.treatmentPlanService.treatmentPlanUpdated$.next(this.treatmentPlan);
      }
    });
  }

  onDeleteMultiple(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    // TODO: Can't delete those that have been scheduled or Paid
    const dialogRef = this.confirmDialog.open(GenericDialogComponent, {
      width: '330px',
      data: {
        title: 'Delete this package?',
        content: 'Are you sure you want to delete this package of multiples?',
        confirmButtonText: 'Yes',
        showCancel: true,
        plannedTreatmentMultipleId: plannedTreatmentMultiple.id,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        if (result === 'confirm') {
          this.loading = true;
          this.treatmentPlanService
            .deletePlannedTreatmentMultiple(plannedTreatmentMultiple)
            .pipe(takeUntil(this.unsub))
            .subscribe(
              (res) => {
                const indexToRemove = this.treatmentPlan.plannedTreatmentMultiples.findIndex(
                  (p) => p.id === plannedTreatmentMultiple.id
                );
                this.treatmentPlan.plannedTreatmentMultiples.splice(indexToRemove, 1);
                this.treatmentPlanService.treatmentPlanUpdated$.next(this.treatmentPlan);
                this.loading = false;
              },
              (errorResponse: HttpErrorResponse) => {
                this.errors = errorResponse.error.errors;
                this.loading = false;
              }
            );
        }
      });
  }

  canMultipleBeDeleted(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    //do not allow deleting pt multiple just because theinvoice was unlocked - make sure none of the tx plans have been used
    return (
      plannedTreatmentMultiple.paymentStatus === PaymentStatus.Unpaid &&
      plannedTreatmentMultiple.plannedTreatments.find((pt) => pt.treatmentState != TreatmentState.Unplanned) == null
    );
  }

  onPayForMultiple(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    this.onGoToInvoice(plannedTreatmentMultiple);
  }

  onGoToInvoice(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    this.loading = true;
    // The Service creates an Invoice if it doesn't exist
    this.invoicesService
      .getInvoiceByPlannedTreatmentMultipleId(plannedTreatmentMultiple.id, InvoiceType.Regular)
      .pipe(takeUntil(this.unsub))
      .subscribe(
        (invoice: Invoice) => {
          this.loading = false;
          // Goto the Invoice
          this.router.navigate(['patientaccounttab/invoice', invoice.id], { relativeTo: this.route.parent.parent });
        },
        (errorResponse: HttpErrorResponse) => {
          this.errors = errorResponse.error.errors;
          this.loading = false;
        }
      );
  }

  getQuantityAvailable(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    if (isNullOrUndefined(plannedTreatmentMultiple)) {
      return '';
    }
    // How many scheduled ones do we have already?
    const totalPlannedTreatmentsCount = plannedTreatmentMultiple.plannedTreatments.length;
    if (totalPlannedTreatmentsCount > 0) {
      const availablePlannedTreatments = plannedTreatmentMultiple.plannedTreatments.filter((pt) => {
        return pt.treatmentState == TreatmentState.Unplanned;
      });
      const usage = availablePlannedTreatments.length + ' / ' + totalPlannedTreatmentsCount;
      return usage;
    }
  }

  onEditMultiple(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    var serviceTemplate = this.serviceTemplateMultiples.filter(
      (v) => v.id == plannedTreatmentMultiple.serviceTemplateId
    )[0];

    const dialogRef = this.createPackageDialog.open(CreateTreatmentPlanPackageComponent, {
      panelClass: 'custom-dialog-container',
      width: '500px',
      data: {
        isNew: false,
        patientId: this.patientId,
        serviceTemplateMultiples: this.serviceTemplateMultiples,
        serviceTemplate: serviceTemplate,
        treatmentPlanId: this.treatmentPlan.id,
        treatmentPlan: this.treatmentPlan,
        plannedTreatmentMultiple: plannedTreatmentMultiple,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.treatmentPlanService.treatmentPlanUpdated$.next(this.treatmentPlan);
      }
    });
  }

  isEditable(plannedTreatmentMultiple: PlannedTreatmentMultiple) {
    return plannedTreatmentMultiple.paymentStatus === PaymentStatus.Unpaid;
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
